import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logAdminAction } from "@/admin/services/adminService";
import { useAdminAuth } from "./useAdminAuth";
import type { AdminUserRow, UserStatus } from "@/admin/types/admin";

export function useAdminUsers(search?: string) {
  const queryClient = useQueryClient();
  const { adminUserId } = useAdminAuth();

  const query = useQuery({
    queryKey: ["admin-users", search],
    queryFn: async (): Promise<AdminUserRow[]> => {
      const { data, error } = await supabase.rpc("admin_get_users" as any);
      if (error) throw error;

      let rows = (data ?? []) as any[];

      if (search && search.trim().length > 0) {
        const q = search.toLowerCase();
        rows = rows.filter(
          (r: any) =>
            r.display_name?.toLowerCase().includes(q) ||
            r.email?.toLowerCase().includes(q)
        );
      }

      return rows.map((row: any) => ({
        id: row.profile_id,
        userId: row.user_id,
        fullName: row.display_name || `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || "Inconnu",
        email: row.email ?? "",
        role: (row.role ?? "client") as AdminUserRow["role"],
        status: (row.status ?? "active") as UserStatus,
        suspendedAt: row.suspended_at ?? null,
        suspensionReason: row.suspension_reason ?? null,
        deletedAt: row.deleted_at ?? null,
        createdAt: row.created_at,
        avatarUrl: row.avatar_url ?? null,
        wilaya: row.wilaya ?? null,
        specialite: row.specialite ?? null,
        verificationStatus: row.verification_status ?? null,
      }));
    },
  });

  const suspendMutation = useMutation({
    mutationFn: async ({
      profileId,
      userId,
      reasonCode,
      notes,
    }: {
      profileId: string;
      userId: string;
      reasonCode: string;
      notes?: string;
    }) => {
      const now = new Date().toISOString();
      const { data: before } = await supabase
        .from("profiles")
        .select("status, suspended_at, suspension_reason")
        .eq("id", profileId)
        .single();

      const { error } = await supabase
        .from("profiles")
        .update({
          status: "suspended",
          suspended_at: now,
          suspended_by_admin_id: adminUserId,
          suspension_reason: reasonCode,
        })
        .eq("id", profileId);
      if (error) throw error;
      return { profileId, userId, reasonCode, notes, before };
    },
    onSuccess: async ({ profileId, reasonCode, notes, before }) => {
      if (adminUserId) {
        await logAdminAction({
          adminUserId,
          actionType: "user_suspended",
          targetType: "profile",
          targetId: profileId,
          reasonCode,
          notes,
          beforeState: before as Record<string, unknown>,
          afterState: { status: "suspended" },
        });
      }
      toast.success("Utilisateur suspendu");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: () => toast.error("Erreur lors de la suspension"),
  });

  const reactivateMutation = useMutation({
    mutationFn: async (profileId: string) => {
      const { error } = await supabase
        .from("profiles")
        .update({
          status: "active",
          suspended_at: null,
          suspended_by_admin_id: null,
          suspension_reason: null,
        })
        .eq("id", profileId);
      if (error) throw error;
      return profileId;
    },
    onSuccess: async (profileId) => {
      if (adminUserId) {
        await logAdminAction({
          adminUserId,
          actionType: "user_reactivated",
          targetType: "profile",
          targetId: profileId,
          afterState: { status: "active" },
        });
      }
      toast.success("Utilisateur réactivé");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: () => toast.error("Erreur lors de la réactivation"),
  });

  const deleteMutation = useMutation({
    mutationFn: async ({
      profileId,
      reasonCode,
      notes,
    }: {
      profileId: string;
      reasonCode: string;
      notes?: string;
    }) => {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("profiles")
        .update({
          status: "deleted",
          deleted_at: now,
          deleted_by_admin_id: adminUserId,
        })
        .eq("id", profileId);
      if (error) throw error;
      return { profileId, reasonCode, notes };
    },
    onSuccess: async ({ profileId, reasonCode, notes }) => {
      if (adminUserId) {
        await logAdminAction({
          adminUserId,
          actionType: "user_deleted",
          targetType: "profile",
          targetId: profileId,
          reasonCode,
          notes,
          afterState: { status: "deleted" },
        });
      }
      toast.success("Utilisateur supprimé (soft delete)");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });

  return { query, suspendMutation, reactivateMutation, deleteMutation };
}
